import { QueryFactory, useEnabledDataBlockTypes } from '@lmt/shared-types';
import { DataBlockFieldDto, DataBlockVariant } from '@lmt/shared-types';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loading } from '../common/Loading';
import { UIEditorLazy } from '../editor/UIEditorLazy';
import {
  FieldBlockConfiguration,
  UIEditorField,
} from '../editor/UIEditorTypes';
import {
  getDataBlockTypeNameForLocalization,
  getUIConfig,
  patientInformationDataBlockTypeName,
} from './common';
import { defaultUiConfig } from './default-ui-config';
import {
  ToolbarComponent,
  useDataBlockConfigToolbarParams,
} from './ToolbarComponent';
import {
  ExtendedFieldsConfigurationField,
  caseHardcodedFields,
  patientHardcodedFields,
  useHardcodedFieldsConfiguration,
  visitHardcodedFields,
} from './hardcoded-fields';
import styles from './DataBlockConfig.module.scss';

function filterDeactivatedFields(fields: DataBlockFieldDto[]) {
  return fields.filter((field: DataBlockFieldDto) => !field.isDeactivated);
}

const hiddenFields = ['Title', 'Level of care'];

export const DataBlockConfig: FC = React.memo(function DataBlockConfig() {
  const i18next = useTranslation();
  const claims = useClaims();

  const dataBlockTypes = useEnabledDataBlockTypes();

  const [params] = useDataBlockConfigToolbarParams();
  const dataBlockTypeId = params.dataBlockTypeId;
  const selectedDataBlockVariant = params.dataBlockVariant;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [resetConfigKey, setConfigKey] = useState<string>('');
  const caseSharedDataBlockTypeId = useMemo(() => {
    return dataBlockTypes.find(
      (type) =>
        type.variant === DataBlockVariant.SharedFields && type.value === 'Case',
    )?.id;
  }, [dataBlockTypes]);
  const patientSharedDataBlockTypeId = useMemo(() => {
    return dataBlockTypes.find(
      (type) =>
        type.variant === DataBlockVariant.SharedFields &&
        type.value === 'Patient',
    )?.id;
  }, [dataBlockTypes]);
  const visitSharedDataBlockTypeId = useMemo(() => {
    return dataBlockTypes.find(
      (type) =>
        type.variant === DataBlockVariant.SharedFields &&
        type.value === 'Visit',
    )?.id;
  }, [dataBlockTypes]);

  const fieldsQuery = QueryFactory.DataBlockFieldQuery.useGetQuery(
    { dataBlockTypeId },
    {
      select: filterDeactivatedFields,
      enabled: !!dataBlockTypeId,
    },
  );

  const caseSharedFieldsQuery = QueryFactory.DataBlockFieldQuery.useGetQuery(
    { dataBlockTypeId: caseSharedDataBlockTypeId! },
    {
      select: filterDeactivatedFields,
      enabled: !!caseSharedDataBlockTypeId,
    },
  );

  const caseHardcodedProperties = useHardcodedFieldsConfiguration(
    caseHardcodedFields,
    { enabled: !!caseSharedDataBlockTypeId },
  );

  const caseSharedFields = useMemo(() => {
    return [
      ...(caseSharedFieldsQuery.data ?? []),
      ...caseHardcodedProperties?.filter((x) => x.name !== 'NumberSource'),
    ].map(convertToUiEditorField);
  }, [caseSharedFieldsQuery.data, caseHardcodedProperties]);

  const visitSharedFieldsQuery = QueryFactory.DataBlockFieldQuery.useGetQuery(
    { dataBlockTypeId: visitSharedDataBlockTypeId! },
    {
      select: filterDeactivatedFields,
      enabled: !!visitSharedDataBlockTypeId,
    },
  );

  const visitHardcodedProperties = useHardcodedFieldsConfiguration(
    visitHardcodedFields,
    { enabled: !!visitSharedDataBlockTypeId },
  );

  const visitSharedFields = useMemo(() => {
    return [
      ...(visitSharedFieldsQuery.data ?? []).filter(
        (x) => x.name !== 'absence_reason',
      ),
      ...visitHardcodedProperties,
    ].map(convertToUiEditorField);
  }, [visitSharedFieldsQuery.data, visitHardcodedProperties]);

  const patientSharedFieldsQuery = QueryFactory.DataBlockFieldQuery.useGetQuery(
    { dataBlockTypeId: patientSharedDataBlockTypeId! },
    {
      select: filterDeactivatedFields,
      enabled: !!patientSharedDataBlockTypeId,
    },
  );

  const patientHardcodedSharedProperties = useHardcodedFieldsConfiguration(
    patientHardcodedFields,
    { enabled: !!patientSharedDataBlockTypeId },
  );

  const selectedDataBlockType = dataBlockTypes.find(
    (m) => m.id === dataBlockTypeId,
  );

  const patientSharedFields = useMemo(() => {
    return [
      ...(patientSharedFieldsQuery.data ?? []),
      ...patientHardcodedSharedProperties?.filter(
        (x) => x.name !== 'NumberSource',
      ),
    ].map(convertToUiEditorField);
  }, [
    patientSharedFieldsQuery.data,
    patientHardcodedSharedProperties,
    dataBlockTypeId,
  ]);

  const dataBlockFieldGroups: FieldBlockConfiguration[] = useMemo(() => {
    const result: FieldBlockConfiguration[] = [];
    if (selectedDataBlockVariant === DataBlockVariant.Patient) {
      if (patientSharedDataBlockTypeId) {
        result.push({
          title: i18next.t('editor.toolbox.patient_values'),
          fields:
            selectedDataBlockType?.value === patientInformationDataBlockTypeName
              ? (
                  patientSharedFieldsQuery.data?.filter(
                    (x) => !hiddenFields.includes(x.name),
                  ) ?? []
                ).map(convertToUiEditorField)
              : patientSharedFields,
          dataBlockTypeId: patientSharedDataBlockTypeId,
          isModificationAllowed: claims.permissions.editMeasurementMappings,
          isShared: true,
        });
      }
      return result;
    }
    if (dataBlockTypeId) {
      result.push({
        title: i18next.t('editor.toolbox.input_values'),
        dataBlockTypeId: dataBlockTypeId,
        fields: (fieldsQuery.data ?? []).map((field) => {
          return {
            key: field.name,
            type: field.dataType,
            dataBlockFieldId: field.id,
            isOptionAllowed: field.isOptionAllowed,
            isSingleValue: field.isSingleValue,
          };
        }),
        isModificationAllowed: claims.permissions.editMeasurementMappings,
        isShared: false,
      });
    }
    if (
      selectedDataBlockVariant === DataBlockVariant.Case &&
      caseSharedDataBlockTypeId
    ) {
      result.push({
        title: i18next.t('editor.toolbox.shared_values'),
        fields: caseSharedFields,
        dataBlockTypeId: caseSharedDataBlockTypeId,
        isModificationAllowed: claims.permissions.editMeasurementMappings,
        isShared: true,
      });
    } else if (
      selectedDataBlockVariant === DataBlockVariant.Visit &&
      visitSharedDataBlockTypeId
    ) {
      result.push({
        title: 'visit fields',
        fields: visitSharedFields,
        dataBlockTypeId: visitSharedDataBlockTypeId,
        isModificationAllowed: claims.permissions.editMeasurementMappings,
        isShared: true,
      });
    }
    return result;
  }, [
    fieldsQuery.data,
    selectedDataBlockVariant,
    dataBlockTypeId,
    caseSharedDataBlockTypeId,
    visitSharedDataBlockTypeId,
    caseSharedFields,
    visitSharedFields,
    patientSharedFields,
    patientSharedFieldsQuery.data,
    selectedDataBlockType,
    i18next.i18n.language,
  ]);

  const configQuery = QueryFactory.DataBlockTypeQuery.useGetUIConfigsQuery(
    dataBlockTypeId!,
    { enabled: !!dataBlockTypeId && !!params.uiConfigType },
  );

  // null is a valid return value in which case we shall show defaultUiConfig
  const config = !configQuery.data
    ? undefined
    : (getUIConfig(configQuery.data, params.uiConfigType) ?? defaultUiConfig);

  return (
    <div className={styles.container} data-test-id="data-block-ui-editor">
      <div className={styles.title} data-test-id="page-title">
        {i18next.t('tabs.data_blocks.settings.title')}
      </div>
      <Loading loading={isLoading} wrapperClassName={'overflowAuto'}>
        <div className={styles.editorContainer}>
          <UIEditorLazy
            fieldBlocks={dataBlockFieldGroups}
            dataBlockTypeNameForLocalization={getDataBlockTypeNameForLocalization(
              selectedDataBlockType,
            )}
            dataBlockType={selectedDataBlockType}
            isInDesignerMode={true}
            config={config}
            configKey={`${dataBlockTypeId}_${params.uiConfigType}_${config}_${resetConfigKey}`}
            toolbarComponent={ToolbarComponent}
            setIsLoading={setIsLoading}
            setConfigKey={setConfigKey}
          />
        </div>
      </Loading>
    </div>
  );
});

function convertToUiEditorField(
  field: ExtendedFieldsConfigurationField,
): UIEditorField {
  return {
    key: field.name,
    type: field.dataType,
    dataBlockFieldId: field.id,
    isSingleValue: field.isSingleValue,
    isOptionAllowed: field.isOptionAllowed,
    dataBlockDictionaryTitle: field.dictionaryTitle,
    isRequired: field.isRequired,
  };
}
